import { Plus, Trash2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import {
  ACCEPTED_ANSWER_MAX,
  ACCEPTED_ANSWERS_MAX,
  ASSESSMENT_QUESTION_TYPE_LABELS,
  ASSESSMENT_QUESTION_TYPES,
  QUESTION_DIFFICULTIES,
  QUESTION_DIFFICULTY_LABELS,
  QUESTION_OPTIONS_MAX,
  QUESTION_POINTS_MAX,
  QUESTION_TAG_MAX,
  QUESTION_TAGS_MAX,
  isAutoMarked,
  isChoiceQuestion,
  questionProblems,
  type AssessmentQuestionType,
  type QuestionDifficulty,
  type QuestionInput,
  type QuestionView,
} from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Checkbox } from '../../../components/Checkbox';
import { Drawer } from '../../../components/Drawer';
import { Field, FieldRow } from '../../../components/Field';
import { Input, Textarea } from '../../../components/Input';
import { MoreOptions } from '../../../components/MoreOptions';
import { Segmented } from '../../../components/Segmented';
import { Select } from '../../../components/Select';
import { TagInput } from '../../../components/TagInput';
import { ApiError, errorMessage } from '../../../lib/api-client';
import { useToasts } from '../../../stores/toasts';
import { wholeNumber } from '../../../lib/whole-number';
import { useQuestionFacets, useSaveQuestion } from '../api/assessments-api';
import styles from './Assessments.module.css';

interface OptionDraft {
  readonly rowId: string;
  readonly text: string;
  readonly isCorrect: boolean;
}

interface QuestionDraft {
  readonly ventureId: string;
  readonly questionText: string;
  readonly type: AssessmentQuestionType;
  readonly points: number;
  readonly difficulty: QuestionDifficulty | null;
  readonly roleFamily: string;
  readonly tags: readonly string[];
  readonly options: readonly OptionDraft[];
  readonly acceptedAnswers: readonly string[];
  readonly explanation: string;
}

const NO_DIFFICULTY = 'none';
const ROLE_FAMILY_SUGGESTIONS = 4;

function newOption(isCorrect = false): OptionDraft {
  return { rowId: crypto.randomUUID(), text: '', isCorrect };
}

function draftOf(question: QuestionView | null, ventureId: string): QuestionDraft {
  if (!question) {
    return {
      ventureId,
      questionText: '',
      type: 'single_select',
      points: 1,
      difficulty: null,
      roleFamily: '',
      tags: [],
      options: [newOption(true), newOption(), newOption()],
      acceptedAnswers: [],
      explanation: '',
    };
  }
  return {
    ventureId: question.ventureId ?? ventureId,
    questionText: question.questionText,
    type: question.type,
    points: question.points,
    difficulty: question.difficulty,
    roleFamily: question.roleFamily ?? '',
    tags: question.tags,
    options: question.options.map((option) => ({ rowId: option.id, text: option.text, isCorrect: option.isCorrect })),
    acceptedAnswers: question.acceptedAnswers,
    explanation: question.explanation ?? '',
  };
}

function inputOf(draft: QuestionDraft): QuestionInput {
  const isChoice = isChoiceQuestion(draft.type);
  return {
    questionText: draft.questionText.trim(),
    type: draft.type,
    points: draft.points,
    difficulty: draft.difficulty,
    roleFamily: draft.roleFamily.trim() || null,
    tags: [...draft.tags],
    options: isChoice ? draft.options.map((option) => ({ text: option.text.trim(), isCorrect: option.isCorrect })) : [],
    acceptedAnswers: draft.type === 'text' ? [...draft.acceptedAnswers] : [],
    explanation: draft.explanation.trim() || null,
  };
}

interface QuestionEditorProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  /** Null for a new question. */
  readonly question: QuestionView | null;
  readonly ventures: readonly { readonly id: string; readonly name: string }[];
  readonly defaultVentureId: string;
  readonly onSaved?: (question: QuestionView) => void;
}

/**
 * ASM-01's editor (F9.01), in a side panel so the bank stays in view. Saving an
 * existing question writes its next version: tests and past attempts keep the wording
 * they had (ADR 0019).
 */
export function QuestionEditor(props: QuestionEditorProps): ReactNode {
  if (!props.isOpen) return null;
  return <Editor key={props.question?.id ?? 'new'} {...props} />;
}

function Editor({ isOpen, onClose, question, ventures, defaultVentureId, onSaved }: QuestionEditorProps): ReactNode {
  const [draft, setDraft] = useState<QuestionDraft>(() => draftOf(question, defaultVentureId));
  const [serverErrors, setServerErrors] = useState<Readonly<Record<string, string>>>({});
  const save = useSaveQuestion();
  const facets = useQuestionFacets();
  const show = useToasts((state) => state.show);
  const isChoice = isChoiceQuestion(draft.type);
  const input = inputOf(draft);
  const problems = questionProblems(input);
  const problemFor = (field: string): string | undefined => problems.find((problem) => problem.field === field)?.message ?? serverErrors[`question.${field}`];
  const isValid = input.questionText !== '' && problems.length === 0 && draft.points >= 1 && (!isChoice || draft.options.every((option) => option.text.trim() !== ''));

  function change(patch: Partial<QuestionDraft>): void {
    setDraft((current) => ({ ...current, ...patch }));
  }

  function setType(type: AssessmentQuestionType): void {
    // "One answer" keeps at most one ticked choice; the first stays right.
    const firstRight = draft.options.findIndex((option) => option.isCorrect);
    const options = type === 'single_select' ? draft.options.map((option, index) => ({ ...option, isCorrect: index === Math.max(0, firstRight) })) : draft.options;
    change({ type, options: isChoiceQuestion(type) && options.length === 0 ? [newOption(true), newOption()] : options });
  }

  function setOption(index: number, patch: Partial<OptionDraft>): void {
    change({ options: draft.options.map((option, i) => (i === index ? { ...option, ...patch } : option)) });
  }

  function markRight(index: number, isCorrect: boolean): void {
    if (draft.type === 'single_select') {
      change({ options: draft.options.map((option, i) => ({ ...option, isCorrect: i === index })) });
      return;
    }
    setOption(index, { isCorrect });
  }

  async function onSave(): Promise<void> {
    setServerErrors({});
    try {
      const saved = await save.mutateAsync({ id: question?.id ?? null, version: question?.version ?? 0, ventureId: draft.ventureId, question: input });
      show({
        tone: 'success',
        message: question ? `Question saved as version ${saved.versionNumber}` : 'Question added to the bank',
        detail: question && question.testsUsing > 0 ? 'Tests keep the wording they had until they are next saved. Past answers never change.' : undefined,
      });
      onSaved?.(saved);
      onClose();
    } catch (cause) {
      setServerErrors(cause instanceof ApiError ? cause.fields : {});
      show({ tone: 'danger', message: 'The question was not saved', detail: errorMessage(cause) });
    }
  }

  const marking = markingNote(draft.type, draft.acceptedAnswers);

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={question ? 'Edit question' : 'New question'}
      meta={question ? `Version ${question.versionNumber}. Saving makes version ${question.versionNumber + 1}` : 'Added to the bank for everyone in the venture'}
      footer={
        <div className={styles.drawerFooter}>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="solid" disabled={!isValid} isPending={save.isPending} onClick={() => void onSave()}>
            {question ? `Save as version ${question.versionNumber + 1}` : 'Add question'}
          </Button>
        </div>
      }
    >
      <div className={styles.form}>
        {!question && ventures.length > 1 && (
          <Field label="Venture" isRequired>
            {({ inputId }) => (
              <Select id={inputId} label="Venture" value={draft.ventureId} options={ventures.map((venture) => ({ value: venture.id, label: venture.name }))} onChange={(ventureId) => change({ ventureId })} />
            )}
          </Field>
        )}
        <Field label="Question" isRequired error={serverErrors['question.questionText']}>
          {({ inputId, isInvalid }) => (
            <Textarea id={inputId} isInvalid={isInvalid} rows={3} autoFocus value={draft.questionText} placeholder="Which formula adds up a column of numbers?" onChange={(event) => change({ questionText: event.target.value })} />
          )}
        </Field>
        <FieldRow>
          <Field label="Kind of answer" hint={marking}>
            {({ inputId, describedBy }) => (
              <Select
                id={inputId}
                describedBy={describedBy}
                label="Kind of answer"
                value={draft.type}
                options={ASSESSMENT_QUESTION_TYPES.map((type) => ({ value: type, label: ASSESSMENT_QUESTION_TYPE_LABELS[type] }))}
                onChange={(value) => setType(asType(value))}
              />
            )}
          </Field>
          <Field label="Marks" error={serverErrors['question.points']}>
            {({ inputId, isInvalid }) => (
              <Input id={inputId} isInvalid={isInvalid} isNumeric inputMode="numeric" value={String(draft.points)} onChange={(event) => change({ points: Math.min(QUESTION_POINTS_MAX, wholeNumber(event.target.value)) })} />
            )}
          </Field>
        </FieldRow>

        {isChoice && (
          <fieldset className={styles.choices}>
            <legend className={styles.legend}>{draft.type === 'single_select' ? 'Choices — tick the right one' : 'Choices — tick every right one'}</legend>
            {draft.options.map((option, index) => (
              <div key={option.rowId} className={styles.choice}>
                <Checkbox checked={option.isCorrect} ariaLabel={`Choice ${index + 1} is right`} onChange={(checked) => markRight(index, checked)} />
                <Input aria-label={`Choice ${index + 1}`} value={option.text} placeholder={`Choice ${index + 1}`} onChange={(event) => setOption(index, { text: event.target.value })} />
                {draft.options.length > 2 && (
                  <Button size="sm" variant="ghost" icon={Trash2} aria-label={`Remove choice ${index + 1}`} onClick={() => change({ options: draft.options.filter((_, i) => i !== index) })} />
                )}
              </div>
            ))}
            {problemFor('options') && <p className={styles.error}>{problemFor('options')}</p>}
            {draft.options.length < QUESTION_OPTIONS_MAX && (
              <Button variant="outline" size="sm" icon={Plus} onClick={() => change({ options: [...draft.options, newOption()] })}>
                Add a choice
              </Button>
            )}
          </fieldset>
        )}

        {draft.type === 'text' && (
          <Field label="Accepted answers" isOptional hint="Press Enter after each. Capitals, spaces and a final full stop are ignored. Leave empty and a person marks it." error={problemFor('acceptedAnswers')}>
            {({ inputId, describedBy, isInvalid }) => (
              <TagInput id={inputId} describedBy={describedBy} isInvalid={isInvalid} value={draft.acceptedAnswers} maxItems={ACCEPTED_ANSWERS_MAX} maxLength={ACCEPTED_ANSWER_MAX} placeholder="300" onChange={(acceptedAnswers) => change({ acceptedAnswers })} />
            )}
          </Field>
        )}

        {/* R9 (QoL §7 rule 4): for finding the question in the bank later — most people skip them. */}
        <MoreOptions label="Difficulty, skills and a note for the marker" isInitiallyOpen={draft.difficulty !== null || draft.tags.length > 0 || draft.roleFamily !== '' || draft.explanation !== ''}>
          <div className={styles.moreFields}>
            <Field label="Difficulty">
              {() => (
                <Segmented
                  label="Difficulty"
                  value={draft.difficulty ?? NO_DIFFICULTY}
                  options={[{ value: NO_DIFFICULTY, label: '—' }, ...QUESTION_DIFFICULTIES.map((difficulty) => ({ value: difficulty, label: QUESTION_DIFFICULTY_LABELS[difficulty] }))]}
                  onChange={(value: string) => change({ difficulty: asDifficulty(value) })}
                />
              )}
            </Field>
            <FieldRow>
              <Field label="Skills" isOptional hint="Press Enter after each.">
                {({ inputId, describedBy }) => (
                  <TagInput id={inputId} describedBy={describedBy} value={draft.tags} maxItems={QUESTION_TAGS_MAX} maxLength={QUESTION_TAG_MAX} placeholder="Excel" suggestions={facets.data?.tags ?? []} onChange={(tags) => change({ tags })} />
                )}
              </Field>
              <Field label="Role family" isOptional>
                {({ inputId }) => (
                  <div className={styles.stack}>
                    <Input id={inputId} value={draft.roleFamily} placeholder="Finance" onChange={(event) => change({ roleFamily: event.target.value })} />
                    <div className={styles.suggestions}>
                      {(facets.data?.roleFamilies ?? [])
                        .filter((family) => family !== draft.roleFamily && family.toLowerCase().includes(draft.roleFamily.trim().toLowerCase()))
                        .slice(0, ROLE_FAMILY_SUGGESTIONS)
                        .map((family) => (
                          <button key={family} type="button" className={styles.suggestion} onClick={() => change({ roleFamily: family })}>
                            {family}
                          </button>
                        ))}
                    </div>
                  </div>
                )}
              </Field>
            </FieldRow>
            <Field label="Note for whoever marks it" isOptional hint="Never shown to candidates.">
              {({ inputId, describedBy }) => (
                <Textarea id={inputId} aria-describedby={describedBy} rows={2} value={draft.explanation} placeholder="A good answer mentions the bank's own record and the ledger." onChange={(event) => change({ explanation: event.target.value })} />
              )}
            </Field>
          </div>
        </MoreOptions>
      </div>
    </Drawer>
  );
}

function markingNote(type: AssessmentQuestionType, acceptedAnswers: readonly string[]): string {
  if (isAutoMarked(type, acceptedAnswers)) return 'Marked by the system as soon as the test is submitted.';
  if (type === 'text') return 'No accepted answers listed, so a person marks it.';
  return 'A person marks it. A test with this question never moves anyone on by itself.';
}

function asType(value: string): AssessmentQuestionType {
  return ASSESSMENT_QUESTION_TYPES.find((type) => type === value) ?? 'single_select';
}

function asDifficulty(value: string): QuestionDifficulty | null {
  return QUESTION_DIFFICULTIES.find((difficulty) => difficulty === value) ?? null;
}
